import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../screens/FormEditorPage.css';
import { baseUrl } from '../utils/config';
import {
    Box,
    Button,
    Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

const FormEditorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(null);
  const [editedData, setEditedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();

  useEffect(() => {
    const fetchFormData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${baseUrl}/api/form-templates/${id}`);
        setFormData(response.data);
        setEditedData(response.data.data.map(row => ({
          ...row,
          cells: row.cells.map(cell => (cell ? { ...cell } : null))
        })));
        console.log('Fetched Form Data:', response.data);
      } catch (err) {
        console.error('Error fetching form data:', err);
        if (err.response && err.response.status === 404) {
          setError('Form not found. It might have been deleted or the ID is incorrect.');
        } else if (err.response && err.response.status === 400) {
          setError('Invalid form ID provided.');
        } else {
          setError('Failed to load form data. Please check your network and try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchFormData();
    }
  }, [id]);

  const handleCellChange = (rowIndex, cellIndex, value) => {
    setEditedData(prevEditedData => {
      const updatedData = [...prevEditedData];
      if (updatedData[rowIndex] && updatedData[rowIndex].cells[cellIndex]) {
        updatedData[rowIndex].cells[cellIndex] = {
          ...updatedData[rowIndex].cells[cellIndex],
          value: value
        };
      } else {
        console.warn(`Attempted to edit a non-existent cell at [${rowIndex}, ${cellIndex}]`);
      }
      return updatedData;
    });
  };

  const handleSaveChanges = async () => {
    console.log('Saving changes:', editedData);
    try {
      await axios.put(`${baseUrl}/api/form-templates/${id}`, { data: editedData });
      alert('Form data saved successfully!');
    } catch (err) {
      console.error('Error saving form data:', err);
      alert('Failed to save form data. Please try again.');
    }
  };

  // MODIFIED: Function to handle viewing the associated image in a pop-up
  const handleViewImage = () => {
    if (formData && formData.associatedImage) {
      const imageUrl = `${baseUrl}${formData.associatedImage}`;
      // Open in a new pop-up window with specific dimensions and features
      window.open(imageUrl, 'ImagePopUp', 'width=800,height=600,resizable=yes,scrollbars=yes');
    } else {
      alert('No image associated with this form.');
    }
  };

   

  if (loading) {
    return (
      <div className="form-editor-container">
        <p>Loading form data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="form-editor-container error-message">
        <h2>Error</h2>
        <p>{error}</p>
        <button className="cancel-button" onClick={() => navigate(-1)}>
          Go Back
        </button>
      </div>
    );
  }

  if (!formData || editedData.length === 0) {
    return (
      <div className="form-editor-container">
        <p>No form data available for editing.</p>
        <button className="cancel-button" onClick={() => navigate(-1)}>
          Go Back
        </button>
      </div>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        minHeight: 'calc(100vh - 64px)',
        backgroundColor: '#f0f2f5',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(4),
      }}
    >
      <div className="form-editor-container">
        <h1>{formData.formName || formData.fileName || 'Untitled Form'}</h1>

       

        {/* View Image Button */}
        {formData.associatedImage && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleViewImage} // This button now opens a pop-up
            sx={{ mb: 2 }}
          >
            View Associated Image
          </Button>
        )}

        {/* Render the table */}
        <div className="table-editable-container">
          <table>
            <tbody>
              {editedData.map((rowObj, rowIndex) => (
                <tr key={`data-row-${rowIndex}`} className={rowObj.isRowBold ? 'bold-row' : ''}>
                  {rowObj.cells.map((cell, cellIndex) => {
                    if (cell === null || (cell.mergeRange && !cell.isMergedTopLeft)) {
                      return null;
                    }

                    const cellStyle = {
                      fontWeight: cell.isCellBold ? 'bold' : 'normal',
                      backgroundColor: (cell.fillColor && cell.fillColor === "#FF00") ? '#C5FFF8' : cell.fillColor,
                      color: cell.fontColor || 'inherit',
                    };

                    return (
                      <td
                        key={`data-cell-${rowIndex}-${cellIndex}`}
                        colSpan={cell.colspan || 1}
                        rowSpan={cell.rowspan || 1}
                        style={cellStyle}
                      >
                        <input
                          type="text"
                          value={cell.value || ''}
                          onChange={(e) => handleCellChange(rowIndex, cellIndex, e.target.value)}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="form-actions">
          <button className="submit-button" onClick={handleSaveChanges}>Save Changes</button>
          <button className="cancel-button" onClick={() => navigate(-1)}>
            Back to List
          </button>
        </div>
      </div>
    </Box>
  );
};

export default FormEditorPage;